import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { notificationService } from '../services/notificationService';

interface NotificationPreferences {
  enabled: boolean;
  maintenance: boolean;
  fuel: boolean;
  mileage: boolean;
}

interface NotificationContextType {
  preferences: NotificationPreferences;
  updatePreferences: (newPreferences: NotificationPreferences) => Promise<void>;
  isLoading: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    enabled: false,
    maintenance: true,
    fuel: true,
    mileage: true
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPreferences = async () => {
      if (currentUser) {
        try {
          // Vérifier si les préférences existent
          const userPrefs = await notificationService.getNotificationPreferences(currentUser.id);
          
          // Si les préférences n'existent pas, les initialiser
          if (!userPrefs) {
            console.log('Initialisation des préférences de notification pour l\'utilisateur:', currentUser.id);
            const defaultPrefs = {
              enabled: false,
              maintenance: true,
              fuel: true,
              mileage: true,
              updatedAt: new Date().toISOString()
            };
            await notificationService.saveNotificationPreferences(currentUser.id, defaultPrefs);
            setPreferences(defaultPrefs);
          } else {
            setPreferences(userPrefs);
            // Si les notifications sont activées, initialiser
            if (userPrefs.enabled) {
              await notificationService.initializeFirebaseMessaging(currentUser.id);
            }
          }
        } catch (error) {
          console.error('Error loading notification preferences:', error);
          if (error instanceof Error) {
            console.error('Détails de l\'erreur:', error.message);
            console.error('Stack trace:', error.stack);
          }
        }
      }
      setIsLoading(false);
    };

    loadPreferences();
  }, [currentUser]);

  const updatePreferences = async (newPreferences: NotificationPreferences) => {
    if (!currentUser) return;

    try {
      await notificationService.saveNotificationPreferences(currentUser.id, newPreferences);
      setPreferences(newPreferences);
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  };

  return (
    <NotificationContext.Provider value={{ preferences, updatePreferences, isLoading }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
